import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Wallet, TrendingUp, Building2, Paperclip, ArrowLeft, Plus, HandCoins, History, Sun, Moon, Sparkles, Trash2, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency, fmtNum } from '@/utils/currency';
import CurrencySymbol from '@/components/CurrencySymbol';
import { toast } from 'sonner';

const THEMES = {
  light: { key: 'light', label: 'Light', icon: Sun },
  dark: { key: 'dark', label: 'Dark', icon: Moon },
  glass: { key: 'glass', label: 'Glass Thermal', icon: Sparkles },
};

const THERMAL_GRADIENTS = [
  'from-rose-500 via-orange-500 to-amber-400',
  'from-cyan-500 via-blue-500 to-indigo-400',
  'from-violet-500 via-fuchsia-500 to-pink-400',
];

export default function AccountingDashboard() {
  const { token } = useContext(AuthContext);
  const [theme, setTheme] = useState(() => localStorage.getItem('dashboard-theme') || 'light');
  const isGlass = theme === 'glass';
  const isDark = theme === 'dark' || theme === 'glass';

  const [data, setData] = useState({ banks: [], total_usd: 0, total_expenses: 0, net_income: 0 });
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSlipDialog, setShowSlipDialog] = useState(false);
  const [showAddBalanceDialog, setShowAddBalanceDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [addBalanceForm, setAddBalanceForm] = useState({
    bank_id: '', account_number: '', amount: '', source_type: 'cash_in_hand', description: '',
    borrower_name: '', borrower_due_date: '', borrower_reminder_days: '7'
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');

  const sourceTypes = [
    { value: 'cash_in_hand', label: 'Cash in Hand' },
    { value: 'capital_injection', label: 'Capital Injection' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => { fetchData(); fetchTransactions(); }, []);
  useEffect(() => { localStorage.setItem('dashboard-theme', theme); }, [theme]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/accounting/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/accounting/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
      setFilteredTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to load transactions');
    }
  };

  const fetchBalanceHistory = async () => {
    try {
      const response = await axios.get(`${API}/accounting/balance-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalanceHistory(response.data);
    } catch (error) {
      console.error('Failed to load balance history');
    }
  };

  const handleDeleteHistoryEntry = async () => {
    if (!deleteTarget || !deleteReason.trim()) { toast.error('Please enter a reason'); return; }
    setSubmitting(true);
    try {
      await axios.delete(`${API}/accounting/balance-history/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { reason: deleteReason.trim() }
      });
      toast.success('Transaction deleted and balance reversed');
      setDeleteTarget(null);
      setDeleteReason('');
      fetchBalanceHistory();
      fetchData();
      fetchTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    finally { setSubmitting(false); }
  };

  const handleAddBalance = async () => {
    if (!addBalanceForm.bank_id || !addBalanceForm.account_number || !addBalanceForm.amount) {
      toast.error('Please select a bank, account, and enter an amount');
      return;
    }
    if (parseFloat(addBalanceForm.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        bank_id: addBalanceForm.bank_id,
        account_number: addBalanceForm.account_number,
        amount: parseFloat(addBalanceForm.amount),
        source_type: addBalanceForm.source_type,
        description: addBalanceForm.description
      };
      if (addBalanceForm.source_type === 'borrowing' && addBalanceForm.borrower_name) {
        payload.borrower_name = addBalanceForm.borrower_name;
        payload.borrower_due_date = addBalanceForm.borrower_due_date ? new Date(addBalanceForm.borrower_due_date).toISOString() : '';
        payload.borrower_reminder_days = parseInt(addBalanceForm.borrower_reminder_days) || 7;
      }
      const res = await axios.post(`${API}/accounting/add-balance`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setShowAddBalanceDialog(false);
      setAddBalanceForm({ bank_id: '', account_number: '', amount: '', source_type: 'cash_in_hand', description: '', borrower_name: '', borrower_due_date: '', borrower_reminder_days: '7' });
      fetchData();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add balance');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedBankObj = data.banks.find(b => b.id === addBalanceForm.bank_id);

  const filterTransactionsByAccount = (bankId, currency) => {
    setFilteredTransactions(transactions.filter(txn => txn.bank_id === bankId && txn.currency === currency));
    setSelectedBank(bankId);
    setSelectedCurrency(currency);
  };

  const clearFilter = () => {
    setFilteredTransactions(transactions);
    setSelectedBank(null);
    setSelectedCurrency(null);
  };

  // Theme tokens
  const pageBg = isGlass ? 'bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-950'
    : theme === 'dark' ? 'bg-slate-950' : 'bg-slate-50';
  const card = isGlass ? 'bg-white/5 backdrop-blur-xl border border-white/10'
    : isDark ? 'bg-slate-900/80 border border-slate-700/50' : '';
  const txt = isDark ? 'text-white' : 'text-slate-900';
  const sub = isGlass ? 'text-white/60' : isDark ? 'text-slate-400' : 'text-slate-600';
  const thBg = isGlass ? 'bg-white/5 border-white/10' : isDark ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-50 border-b';
  const thText = isGlass ? 'text-white/70' : isDark ? 'text-slate-300' : 'text-slate-700';
  const rowCls = isGlass ? 'border-white/5 hover:bg-white/10' : isDark ? 'border-slate-700/50 hover:bg-slate-800/50' : 'border-b hover:bg-slate-50';
  const switcherBg = isDark ? 'bg-white/10' : 'bg-slate-100';

  if (loading) {
    return (
      <Layout>
        <div className={`min-h-screen ${pageBg} transition-colors duration-500`}>
          <div className="p-6 lg:p-8 flex items-center justify-center">
            <p className={sub}>Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const overviewCards = [
    {
      label: 'Total Balance (USD)', value: formatCurrency(data.total_usd),
      icon: Wallet, lightText: 'text-emerald-100',
      gradient: 'from-emerald-500 to-teal-600', thermal: THERMAL_GRADIENTS[0],
      iconOpacity: 'text-white opacity-30',
    },
    {
      label: 'Total Expenses (USD)', value: formatCurrency(data.total_expenses),
      icon: TrendingUp, lightText: 'text-red-100',
      gradient: 'from-red-500 to-rose-600', thermal: THERMAL_GRADIENTS[1],
      iconOpacity: 'text-white opacity-30 rotate-180', extraClass: 'sm:col-span-1',
    },
    {
      label: 'Net Income (USD)', value: formatCurrency(data.net_income),
      icon: TrendingUp, lightText: 'text-blue-100',
      gradient: 'from-blue-500 to-indigo-600', thermal: THERMAL_GRADIENTS[2],
      iconOpacity: 'text-white opacity-30', extraClass: 'sm:col-span-2 lg:col-span-1',
    },
  ];

  return (
    <Layout>
      <div className={`min-h-screen ${pageBg} transition-colors duration-500`}>
        <div className="p-2 sm:p-6 lg:p-8 space-y-2 sm:space-y-6" data-testid="accounting-dashboard">

          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className={`text-lg sm:text-3xl font-bold ${txt} leading-none`}>Accounting Dashboard</h1>
              <p className={`text-[10px] sm:text-base ${sub} mt-0.5 leading-none`}>Track all your bank accounts and transactions</p>
            </div>
            <div className={`flex gap-1 p-1 rounded-lg ${switcherBg}`} data-testid="acct-theme-switcher">
              {Object.values(THEMES).map((th) => {
                const ThIcon = th.icon;
                return (
                  <button
                    key={th.key}
                    onClick={() => setTheme(th.key)}
                    className={`p-2 rounded-md transition-all ${
                      theme === th.key
                        ? (isDark ? 'bg-white/20 text-white' : 'bg-white text-slate-900 shadow-sm')
                        : (isDark ? 'text-white/50 hover:text-white/80' : 'text-slate-400 hover:text-slate-600')
                    }`}
                    title={th.label}
                    data-testid={`acct-theme-${th.key}`}
                  >
                    <ThIcon className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Financial Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {overviewCards.map((c, i) => {
              const Icon = c.icon;
              const cardGradient = isGlass
                ? `bg-gradient-to-br ${c.thermal} border border-white/20 shadow-lg shadow-white/5`
                : `bg-gradient-to-br ${c.gradient}`;
              return (
                <Card key={i} className={`p-3 sm:p-6 text-white ${cardGradient} ${c.extraClass || ''}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className={`${isGlass ? 'text-white/70' : c.lightText} text-[10px] sm:text-sm leading-none mb-1.5`}>{c.label}</p>
                      <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold whitespace-nowrap leading-none">{c.value}</h2>
                    </div>
                    <Icon className={`w-7 h-7 sm:w-12 sm:h-12 ${c.iconOpacity} flex-shrink-0`} />
                  </div>
                  {isGlass && <div className="mt-3 h-0.5 rounded-full bg-white/20" />}
                </Card>
              );
            })}
          </div>

          {/* Income vs Expense Indicator */}
          <Card className={`p-2 sm:p-6 ${card}`}>
            <div className="space-y-1 sm:space-y-2">
              <div className="flex justify-between gap-2 text-[9px] sm:text-sm font-semibold">
                <span className={isGlass ? 'text-emerald-400' : 'text-emerald-600'}>Income: {formatCurrency(data.total_usd)}</span>
                <span className={isGlass ? 'text-rose-400' : 'text-red-600'}>Expenses: {formatCurrency(data.total_expenses)}</span>
              </div>
              <div className={`relative h-4 sm:h-8 ${isGlass ? 'bg-white/10' : isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded-full overflow-hidden`}>
                {(() => {
                  const total = data.total_usd + data.total_expenses;
                  const pct = total > 0 ? (data.total_usd / total) * 100 : 50;
                  const barColor = pct > 75 ? 'bg-gradient-to-r from-emerald-500 to-green-600'
                    : pct > 60 ? 'bg-gradient-to-r from-yellow-400 to-emerald-500'
                    : pct > 40 ? 'bg-gradient-to-r from-orange-400 to-yellow-500'
                    : 'bg-gradient-to-r from-red-500 to-orange-500';
                  return <div className={`absolute left-0 top-0 h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />;
                })()}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-[8px] sm:text-xs font-bold ${isGlass ? 'text-white/80' : isDark ? 'text-white/70' : 'text-slate-700'}`}>
                    {data.net_income >= 0 ? 'Profitable' : 'In Deficit'}
                  </span>
                </div>
              </div>
              <p className={`text-[8px] sm:text-xs ${sub} text-center leading-none`}>
                {data.net_income >= 0
                  ? `You're ${((data.total_usd / (data.total_expenses || 1)) * 100).toFixed(0)}% above expenses`
                  : `Expenses exceed income by ${formatCurrency(Math.abs(data.net_income))}`}
              </p>
            </div>
          </Card>

          {/* Bank Accounts */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-lg sm:text-xl font-bold ${txt}`}>Bank Accounts</h2>
              <div className="flex gap-2">
                <Button
                  variant={isDark ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => { fetchBalanceHistory(); setShowHistoryDialog(true); }}
                  className={isGlass ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : ''}
                  data-testid="balance-history-btn"
                >
                  <History className="w-4 h-4 mr-1" /> History
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowAddBalanceDialog(true)}
                  className={isGlass ? 'bg-emerald-500/80 hover:bg-emerald-500 border-0' : ''}
                  data-testid="add-balance-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Balance
                </Button>
              </div>
            </div>

            {data.banks.length === 0 ? (
              <Card className={`p-6 sm:p-8 text-center ${card}`}>
                <Building2 className={`w-10 h-10 sm:w-12 sm:h-12 ${isGlass ? 'text-white/30' : 'text-slate-400'} mx-auto mb-3`} />
                <p className={`text-sm sm:text-base ${sub}`}>No bank accounts configured</p>
                <p className={`text-xs sm:text-sm ${sub} mt-1`}>Go to Settings to add banks</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.banks.map((bank, bi) => (
                  <Card key={bank.id} className={`p-4 ${card} ${isGlass ? 'hover:bg-white/10 transition-colors' : ''}`}>
                    <div className={`flex items-center gap-3 mb-4 pb-3 ${isGlass ? 'border-b border-white/10' : isDark ? 'border-b border-slate-700' : 'border-b'}`}>
                      {bank.bank_logo && (
                        <img src={bank.bank_logo} alt={bank.bank_name} className={`h-6 sm:h-8 w-auto object-contain ${isDark ? 'brightness-125' : ''}`} />
                      )}
                      <h3 className={`text-sm sm:text-base font-semibold ${txt} truncate`}>{bank.bank_name}</h3>
                    </div>
                    <div className="space-y-2">
                      {bank.accounts.map((account, index) => (
                        <button
                          key={index}
                          onClick={() => filterTransactionsByAccount(bank.id, account.currency)}
                          className={`w-full flex justify-between items-center text-xs sm:text-sm p-2 rounded transition-colors cursor-pointer
                            ${isGlass ? 'hover:bg-white/10' : isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'}`}
                        >
                          <span className={`font-semibold ${isGlass ? 'text-white/80' : isDark ? 'text-slate-300' : 'text-slate-700'}`}>{account.currency}</span>
                          <span className={`font-mono inline-flex items-center ${txt}`}>
                            <CurrencySymbol currency={account.currency} />
                            {fmtNum(account.balance || 0)}
                          </span>
                        </button>
                      ))}
                    </div>
                    {isGlass && <div className={`mt-3 h-0.5 rounded-full bg-gradient-to-r ${THERMAL_GRADIENTS[bi % THERMAL_GRADIENTS.length]} opacity-40`} />}
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Recent Transactions */}
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h2 className={`text-lg sm:text-xl font-bold ${txt}`}>
                {selectedBank && selectedCurrency ? `Transactions - ${selectedCurrency} Account` : 'Recent Transactions'}
              </h2>
              {selectedBank && (
                <Button
                  onClick={clearFilter}
                  variant={isDark ? 'secondary' : 'outline'}
                  size="sm"
                  className={`self-start sm:self-auto ${isGlass ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : ''}`}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Show All Transactions
                </Button>
              )}
            </div>

            {filteredTransactions.length === 0 ? (
              <Card className={`p-6 sm:p-8 text-center ${card}`}>
                <TrendingUp className={`w-10 h-10 sm:w-12 sm:h-12 ${isGlass ? 'text-white/30' : 'text-slate-400'} mx-auto mb-3`} />
                <p className={`text-sm sm:text-base ${sub}`}>
                  {selectedBank ? 'No transactions for this account' : 'No transactions yet'}
                </p>
              </Card>
            ) : (
              <Card className={`overflow-hidden ${card}`}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead className={thBg}>
                      <tr>
                        {['Date', 'Type', 'Reference', 'Method', 'Currency', 'Amount', 'USD', 'Slip'].map((h, i) => (
                          <th key={h} className={`px-3 sm:px-4 py-3 text-xs font-semibold ${thText} ${i >= 5 ? 'text-right' : 'text-left'} ${h === 'Slip' ? 'text-center' : ''}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map((txn) => {
                        const txnDate = txn.created_at || txn.date;
                        const txnType = txn.transaction_type || txn.type || '-';
                        const txnRef = txn.reference_id || txn.expense_id || '-';
                        const txnMethod = txn.payment_method || '-';
                        const txnAmount = txn.amount || 0;
                        const txnAmountUsd = txn.amount_in_usd || 0;
                        return (
                          <tr key={txn.id} className={rowCls}>
                            <td className={`px-3 sm:px-4 py-3 text-xs sm:text-sm ${sub}`}>
                              {txnDate ? new Date(txnDate).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${
                                isGlass
                                  ? (txnType.includes('settlement') ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300')
                                  : (txnType.includes('settlement') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700')
                              }`}>
                                {txnType}
                              </span>
                            </td>
                            <td className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-mono ${txt}`}>{txnRef}</td>
                            <td className={`px-3 sm:px-4 py-3 text-xs sm:text-sm ${sub} capitalize`}>{txnMethod}</td>
                            <td className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-semibold ${isGlass ? 'text-white/80' : isDark ? 'text-slate-300' : 'text-slate-700'}`}>{txn.currency || '-'}</td>
                            <td className={`px-3 sm:px-4 py-3 text-xs sm:text-sm text-right font-mono ${txnAmount < 0 ? 'text-red-500 font-bold' : txt}`}>
                              {fmtNum(txnAmount)}
                            </td>
                            <td className={`px-3 sm:px-4 py-3 text-xs sm:text-sm text-right font-mono ${txnAmountUsd < 0 ? 'text-red-500 font-bold' : isGlass ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              ${fmtNum(txnAmountUsd)}
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-center">
                              {txn.payment_slip ? (
                                <button onClick={() => { setSelectedSlip(txn.payment_slip); setShowSlipDialog(true); }}
                                  className={`${isGlass ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'} transition-colors`} title="View payment slip">
                                  <Paperclip className="w-4 h-4" />
                                </button>
                              ) : (
                                <span className={isGlass ? 'text-white/20' : 'text-slate-300'}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>

          {/* Payment Slip Viewer Dialog */}
          <Dialog open={showSlipDialog} onOpenChange={setShowSlipDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Payment Slip</DialogTitle><DialogDescription className="sr-only">View payment slip</DialogDescription></DialogHeader>
              <div className="flex justify-center">
                {selectedSlip && <img src={selectedSlip} alt="Payment Slip" className="max-w-full max-h-[70vh] object-contain rounded border" />}
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Balance Dialog */}
          <Dialog open={showAddBalanceDialog} onOpenChange={(open) => {
            setShowAddBalanceDialog(open);
            if (!open) setAddBalanceForm({ bank_id: '', account_number: '', amount: '', source_type: 'cash_in_hand', description: '' });
          }}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HandCoins className="w-5 h-5 text-emerald-600" /> Add Balance
                </DialogTitle>
                <DialogDescription className="sr-only">Add balance to a bank account</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium text-slate-500">Bank</Label>
                  <div className="flex gap-2 mt-1">
                    {data.banks.map((bank) => (
                      <div key={bank.id} data-testid={`add-bal-bank-${bank.id}`}
                        onClick={() => setAddBalanceForm(f => ({ ...f, bank_id: bank.id, account_number: '' }))}
                        className={`flex-1 p-2 border rounded cursor-pointer text-center text-sm transition-colors ${
                          addBalanceForm.bank_id === bank.id ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'hover:border-emerald-300'
                        }`}>{bank.bank_name}</div>
                    ))}
                  </div>
                </div>
                {selectedBankObj && (
                  <div>
                    <Label className="text-xs font-medium text-slate-500">Account</Label>
                    <div className="space-y-1 mt-1">
                      {selectedBankObj.accounts.map((acc) => (
                        <div key={acc.account_number} data-testid={`add-bal-acc-${acc.account_number}`}
                          onClick={() => setAddBalanceForm(f => ({ ...f, account_number: acc.account_number }))}
                          className={`flex justify-between items-center p-2 border rounded cursor-pointer text-sm transition-colors ${
                            addBalanceForm.account_number === acc.account_number ? 'border-emerald-500 bg-emerald-50' : 'hover:border-emerald-300'
                          }`}>
                          <span>{acc.account_number}</span>
                          <span className="font-mono text-xs">{acc.currency} <CurrencySymbol currency={acc.currency} />{(acc.balance || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-500">Amount</Label>
                    <Input data-testid="add-balance-amount" type="number" step="0.01" min="0.01"
                      value={addBalanceForm.amount} onChange={(e) => setAddBalanceForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="0.00" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-500">Source</Label>
                    <select data-testid="source-type-select" value={addBalanceForm.source_type}
                      onChange={(e) => setAddBalanceForm(f => ({ ...f, source_type: e.target.value }))}
                      className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                      {sourceTypes.map((src) => <option key={src.value} value={src.value}>{src.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Note (optional)</Label>
                  <Input data-testid="add-balance-description" value={addBalanceForm.description}
                    onChange={(e) => setAddBalanceForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="e.g. Loan from XYZ" className="mt-1" />
                </div>
                <Button data-testid="submit-add-balance" onClick={handleAddBalance}
                  disabled={!addBalanceForm.bank_id || !addBalanceForm.account_number || !addBalanceForm.amount || submitting}
                  className="w-full">{submitting ? 'Adding...' : 'Add Balance'}</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Balance History Dialog */}
          <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-600" /> Balance History
                </DialogTitle>
                <DialogDescription className="sr-only">History of balance additions and loan transactions</DialogDescription>
              </DialogHeader>
              {balanceHistory.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No balance history yet</p>
              ) : (
                <div className="space-y-3">
                  {balanceHistory.map((entry) => {
                    const isLoanCreation = entry.transaction_type === 'loan_creation';
                    const isLoanDeletion = entry.transaction_type === 'loan_deletion';
                    const isBalanceDeletion = entry.transaction_type === 'balance_deletion';
                    const isTransfer = entry.transaction_type === 'transfer';
                    const isDeletionEntry = isLoanDeletion || isBalanceDeletion;
                    const isDebit = entry.amount < 0;
                    return (
                    <Card key={entry.id} className={`p-3 ${
                      isDeletionEntry ? 'border-l-4 border-l-slate-400 bg-slate-50' :
                      isTransfer ? (isDebit ? 'border-l-4 border-l-orange-400' : 'border-l-4 border-l-indigo-400') :
                      isLoanCreation ? (isDebit ? 'border-l-4 border-l-red-400' : 'border-l-4 border-l-emerald-400') : ''
                    }`}
                      data-testid={`history-entry-${entry.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className={`font-semibold text-sm ${isDeletionEntry ? 'line-through text-slate-400' : ''}`}>
                            {entry.bank_name}{entry.bank_name && entry.account_number ? ' - ' : ''}{entry.account_number}
                          </p>
                          <p className="text-xs text-slate-500">
                            <span className={`inline-block px-2 py-0.5 rounded mr-2 ${
                              isDeletionEntry ? 'bg-slate-200 text-slate-600' :
                              isTransfer ? (isDebit ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700') :
                              isLoanCreation
                                ? (isDebit ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700')
                                : 'bg-blue-100 text-blue-700'
                            }`}>{entry.payment_method}</span>
                            {entry.description && <span className="text-slate-600">{entry.description}</span>}
                          </p>
                          {entry.transfer_id && !isDeletionEntry && (
                            <a href="/transfers"
                              className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 hover:bg-indigo-100 transition-colors"
                              data-testid={`transfer-link-${entry.id}`}>
                              <ArrowRightLeft className="w-3 h-3" /> View Transfer &rarr;
                            </a>
                          )}
                          {entry.linked_loan_id && !isDeletionEntry && (
                            <a href={`/loans?highlight=${entry.linked_loan_id}`}
                              className={`inline-flex items-center gap-1 mt-1 text-xs font-medium rounded px-2 py-0.5 transition-colors ${
                                isDebit
                                  ? 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'
                                  : 'text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100'
                              }`}
                              data-testid={`loan-link-${entry.id}`}>
                              <HandCoins className="w-3 h-3" /> {isDebit ? 'View Loan' : 'View Borrowing'} &rarr;
                            </a>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {entry.deleted_by_name || entry.deleted_by
                              ? `Deleted by ${entry.deleted_by_name || entry.deleted_by} on `
                              : entry.added_by_name || entry.added_by
                                ? `By ${entry.added_by_name || entry.added_by} on `
                                : ''}{new Date(entry.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right flex items-start gap-2">
                          <div>
                            <p className={`font-bold ${isDebit ? 'text-red-600' : 'text-emerald-600'}`}>
                              {isDebit ? '' : '+'}<CurrencySymbol currency={entry.currency} />{fmtNum(Math.abs(entry.amount || 0))}
                            </p>
                            <p className="text-xs text-slate-500">${fmtNum(Math.abs(entry.amount_in_usd || 0))} USD</p>
                          </div>
                          {!isDeletionEntry && (
                            <button
                              onClick={() => { setDeleteTarget(entry); setDeleteReason(''); }}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                              title="Delete transaction"
                              data-testid={`delete-history-${entry.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </Card>
                  );})}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Delete History Entry Confirmation */}
          <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteReason(''); } }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="w-5 h-5" /> Delete Transaction
                </DialogTitle>
                <DialogDescription className="sr-only">Confirm deletion of a balance history transaction</DialogDescription>
              </DialogHeader>
              {deleteTarget && (
                <div className="space-y-3">
                  <Card className="p-3 bg-red-50 border-red-200">
                    <p className="text-sm font-medium">{deleteTarget.bank_name} — {deleteTarget.account_number}</p>
                    <p className="text-xs text-slate-600">{deleteTarget.payment_method} · {deleteTarget.description}</p>
                    <p className="text-sm font-bold mt-1">
                      {deleteTarget.amount < 0 ? '' : '+'}<CurrencySymbol currency={deleteTarget.currency} />{fmtNum(Math.abs(deleteTarget.amount || 0))}
                    </p>
                  </Card>
                  <p className="text-xs text-slate-500">
                    This will reverse the bank account balance and record the deletion in Recent Transactions.
                  </p>
                  <div>
                    <Label className="text-xs font-medium text-slate-500">Reason for deletion *</Label>
                    <Input value={deleteReason} onChange={e => setDeleteReason(e.target.value)}
                      placeholder="e.g. Duplicate entry, Incorrect amount"
                      className="mt-1" data-testid="delete-reason-input" />
                  </div>
                  <Button onClick={handleDeleteHistoryEntry} disabled={submitting || !deleteReason.trim()}
                    className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-history">
                    {submitting ? 'Deleting...' : 'Delete & Reverse Balance'}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </Layout>
  );
}
